import { lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { splitLocale } from '../i18n/siteLocales';
import Layout from '../components/Layout';

// Lazy load all pages
const HomePage = lazy(() => import('../pages/HomePage'));
const BlogPage = lazy(() => import('../pages/BlogPage'));
const HelpCenterPage = lazy(() => import('../pages/HelpCenterPage'));
const WebinarsPage = lazy(() => import('../pages/WebinarsPage'));
const PrivacyPolicyPage = lazy(() => import('../pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('../pages/TermsOfServicePage'));
const RefundPolicyPage = lazy(() => import('../pages/RefundPolicyPage'));
const GSTPage = lazy(() => import('../pages/GSTPage'));
const DataProcessingPage = lazy(() => import('../pages/DataProcessingPage'));

// New dedicated pages
const FeaturesPage = lazy(() => import('../pages/FeaturesPage'));
const BenefitsPage = lazy(() => import('../pages/BenefitsPage'));
const PricingPage = lazy(() => import('../pages/PricingPage'));
const ContactPage = lazy(() => import('../pages/ContactPage'));
const ContactSupportPage = lazy(() => import('../pages/ContactSupportPage'));
const SuccessStoriesPage = lazy(() => import('../pages/SuccessStoriesPage'));
const AboutUsPage = lazy(() => import('../pages/AboutUsPage'));
const FreeTrialPage = lazy(() => import('../pages/FreeTrialPage'));
const ForCoachesPage = lazy(() => import('../pages/ForCoachesPage'));
const GymsPage = lazy(() => import('../pages/GymsPage'));
const WhatsNewPage = lazy(() => import('../pages/WhatsNewPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

// Competitor comparison pages
const ComparePage = lazy(() => import('../pages/ComparePage'));
const AlternativesTrainerizePage = lazy(() => import('../pages/AlternativesTrainerizePage'));
const AlternativesTrueCoachPage = lazy(() => import('../pages/AlternativesTrueCoachPage'));
const AlternativesEverfitPage = lazy(() => import('../pages/AlternativesEverfitPage'));
const AlternativesFitBuddPage = lazy(() => import('../pages/AlternativesFitBuddPage'));
const AlternativesWhatsAppSheetsPage = lazy(() => import('../pages/AlternativesWhatsAppSheetsPage'));
const SwitchGuidePage = lazy(() => import('../pages/SwitchGuidePage'));
const AlternativesCompetitorPage = lazy(() => import('../pages/AlternativesCompetitorPage'));
const PricingComparisonPage = lazy(() => import('../pages/PricingComparisonPage'));
const WhiteLabelAppPage = lazy(() => import('../pages/WhiteLabelAppPage'));
const CountryPage = lazy(() => import('../pages/CountryPage'));
const FeaturePage = lazy(() => import('../pages/FeaturePage'));
const FeaturesHubPage = lazy(() => import('../pages/FeaturePage').then((m) => ({ default: m.FeaturesHubPage })));
const CostCalculatorPage = lazy(() => import('../pages/CostCalculatorPage'));
const FoundingCoachPage = lazy(() => import('../pages/FoundingCoachPage'));
const WhyTrainzillaPage = lazy(() => import('../pages/WhyTrainzillaPage'));

// Blog — one data-driven article page (CMS `articles`) + category page
const BlogArticlePage = lazy(() => import('../pages/BlogArticlePage'));
const BlogCategoryPage = lazy(() => import('../pages/BlogCategoryPage'));

// Help pages
const GettingStartedPage = lazy(() => import('../pages/help/GettingStartedPage'));
const FAQPage = lazy(() => import('../pages/help/FAQPage'));
const SupportContactPage = lazy(() => import('../pages/help/SupportContactPage'));

// Additional pages

// Client app landing page
const ClientApp = lazy(() => import('../pages/ClientApp'));

// AI Coach landing page (client-facing AI coach product)
const AICoachClientPage = lazy(() => import('../pages/AICoachClientPage'));

// App privacy policy page
const AppPrivacyPolicy = lazy(() => import('../pages/AppPrivacyPolicy'));

// Marketplace page
const MarketplacePage = lazy(() => import('../pages/MarketplacePage'));

// AI Agent Integration (MCP) page
const AIIntegrationPage = lazy(() => import('../pages/AIIntegrationPage'));
const AgentPage = lazy(() => import('../pages/AgentPage'));

// Webinar pages

export default function AppRouter() {
  return (
    <Routes>
      {/* Public Routes with Layout */}
      <Route path="/" element={<Layout />}>
        {/* Home Page */}
        <Route index element={<HomePage />} />
        
        {/* External Portal Redirects */}
        <Route path="login" element={<Navigate to="https://app.trainzilla.in/login" replace />} />
        <Route path="register" element={<Navigate to="https://app.trainzilla.in/register" replace />} />
        <Route path="signup" element={<Navigate to="https://app.trainzilla.in/register" replace />} />
        <Route path="dashboard" element={<Navigate to="https://app.trainzilla.in/dashboard" replace />} />
        
        {/* Solutions - Main Product Features Page */}
        <Route path="solutions" element={<FeaturesPage />} />
        <Route path="solutions/client-management" element={<FeaturesPage />} />
        <Route path="solutions/workout-planning" element={<FeaturesPage />} />
        <Route path="solutions/progress-tracking" element={<FeaturesPage />} />
        <Route path="solutions/nutrition-planning" element={<FeaturesPage />} />
        <Route path="solutions/business-analytics" element={<FeaturesPage />} />
        <Route path="solutions/payment-management" element={<FeaturesPage />} />
        <Route path="solutions/ai-features" element={<FeaturesPage />} />
        <Route path="solutions/video-features" element={<FeaturesPage />} />
        <Route path="solutions/mobile-features" element={<FeaturesPage />} />
        <Route path="solutions/ai-agent" element={<AIIntegrationPage />} />
        
        {/* AI Integration aliases */}
        <Route path="ai-integration" element={<AIIntegrationPage />} />
        <Route path="ai-agent" element={<AIIntegrationPage />} />
        <Route path="agent" element={<AgentPage />} />
        <Route path="mcp" element={<AIIntegrationPage />} />
        
        {/* Legacy feature routes - redirect to solutions */}
        <Route path="platform" element={<Navigate to="/solutions" replace />} />
        <Route path="client-management" element={<Navigate to="/solutions/client-management" replace />} />
        <Route path="workout-plans" element={<Navigate to="/solutions/workout-planning" replace />} />
        <Route path="progress-tracking" element={<Navigate to="/solutions/progress-tracking" replace />} />
        <Route path="ai-features" element={<Navigate to="/solutions/ai-features" replace />} />
        <Route path="video-features" element={<Navigate to="/solutions/video-features" replace />} />
        <Route path="mobile-features" element={<Navigate to="/solutions/mobile-features" replace />} />
        <Route path="analytics-features" element={<Navigate to="/solutions/business-analytics" replace />} />
        
        {/* Benefits - Industry & Use Case Benefits */}
        <Route path="benefits" element={<BenefitsPage />} />
        <Route path="benefits/personal-trainers" element={<BenefitsPage />} />
        <Route path="benefits/fitness-centers" element={<Navigate to="/gyms" replace />} />
        <Route path="benefits/yoga-instructors" element={<BenefitsPage />} />
        <Route path="benefits/nutritionists" element={<BenefitsPage />} />
        <Route path="benefits/corporate-wellness" element={<BenefitsPage />} />
        
        {/* Legacy benefit routes - redirect to new structure */}
        <Route path="personal-trainers" element={<Navigate to="/benefits/personal-trainers" replace />} />
        <Route path="gym-owners" element={<Navigate to="/gyms" replace />} />
        <Route path="fitness-studios" element={<Navigate to="/gyms" replace />} />
        <Route path="corporate-wellness" element={<Navigate to="/benefits/corporate-wellness" replace />} />
        
        {/* Industry-specific landing pages */}
        <Route path="industries" element={<Navigate to="/benefits" replace />} />
        <Route path="industries/personal-trainers" element={<Navigate to="/benefits/personal-trainers" replace />} />
        <Route path="industries/fitness-centers" element={<Navigate to="/gyms" replace />} />
        <Route path="industries/yoga-instructors" element={<Navigate to="/benefits/yoga-instructors" replace />} />
        <Route path="industries/nutritionists" element={<Navigate to="/benefits/nutritionists" replace />} />
        
        {/* Pricing Navigation - All redirect to Pricing Page */}
        <Route path="pricing" element={<PricingPage />} />
        <Route path="starter-plan" element={<PricingPage />} />
        <Route path="professional-plan" element={<PricingPage />} />
        <Route path="enterprise-plan" element={<PricingPage />} />
        <Route path="plans" element={<Navigate to="/pricing" replace />} />
        
        {/* Resources Navigation */}
        <Route path="resources" element={<Navigate to="/help-center" replace />} />
        <Route path="about-us" element={<AboutUsPage />} />
        <Route path="help-center" element={<HelpCenterPage />} />
        <Route path="help" element={<Navigate to="/help-center" replace />} />
        <Route path="support" element={<Navigate to="/help-center" replace />} />
        <Route path="training-videos" element={<Navigate to="/webinars" replace />} />
        <Route path="training" element={<Navigate to="/webinars" replace />} />
        <Route path="success-stories" element={<SuccessStoriesPage />} />
        <Route path="testimonials" element={<SuccessStoriesPage />} />
        <Route path="contact-support" element={<ContactSupportPage />} />
        <Route path="contact" element={<ContactPage />} />
        
        {/* Help Center Sub-pages */}
        <Route path="help/getting-started" element={<GettingStartedPage />} />
        <Route path="help/client-management" element={<Navigate to="/help-center" replace />} />
        <Route path="help/workout-planning" element={<Navigate to="/help-center" replace />} />
        <Route path="help/progress-tracking" element={<Navigate to="/help-center" replace />} />
        <Route path="help/payments-billing" element={<Navigate to="/help-center" replace />} />
        <Route path="help/faq" element={<FAQPage />} />
        <Route path="help/troubleshooting" element={<Navigate to="/help-center" replace />} />
        <Route path="help/support-contact" element={<SupportContactPage />} />
        <Route path="help/contact" element={<SupportContactPage />} />
        
        {/* Additional Help Pages */}
        <Route path="help/mobile-app" element={<Navigate to="/help-center" replace />} />
        <Route path="help/user-manual" element={<Navigate to="/help-center" replace />} />
        
        {/* Direct route aliases for popular help topics */}
        <Route path="mobile-app-guide" element={<Navigate to="/help-center" replace />} />
        <Route path="user-manual" element={<Navigate to="/help-center" replace />} />
        
        {/* Client App Landing Page */}
        <Route path="client-app" element={<ClientApp />} />
        <Route path="mobile-app" element={<ClientApp />} />
        <Route path="download-app" element={<ClientApp />} />
        <Route path="app" element={<ClientApp />} />

        {/* AI Coach Landing Page (client-facing) */}
        <Route path="ai-coach" element={<AICoachClientPage />} />
        <Route path="ai-fitness-coach" element={<AICoachClientPage />} />

        {/* Marketplace pitch/explainer content — moved off /marketplace, which
            is now a Netlify-level proxy (see netlify.toml) onto the real,
            live coach directory (tz-profile-app). A client-side React Router
            route here can never win against that: the proxy resolves at the
            CDN edge before this app's JS ever loads, for every path under
            /marketplace/*, including nested ones. Filed under /for-coaches
            since this content — "your profile goes live automatically",
            "clients discover you" — pitches the marketplace to a coach
            deciding whether to sign up, the same audience /for-coaches
            already targets. A real merge into that page's own content is a
            content decision for whoever owns that copy, not made here. */}
        <Route path="for-coaches/marketplace" element={<MarketplacePage />} />
        <Route path="marketplace" element={<Navigate to="/for-coaches/marketplace" replace />} />
        
        {/* Help redirects for common variations */}
        <Route path="help/virtual-training" element={<Navigate to="/help/workout-planning" replace />} />
        <Route path="help/billing" element={<Navigate to="/help/payments-billing" replace />} />
        <Route path="help/payments" element={<Navigate to="/help/payments-billing" replace />} />
        <Route path="help/community" element={<Navigate to="/help/faq" replace />} />
        
        {/* Webinars */}
        <Route path="webinars" element={<WebinarsPage />} />
        <Route path="webinars/*" element={<Navigate to="/webinars" replace />} />
        
        {/* Blog & Content */}
        <Route path="blog" element={<BlogPage />} />
        
        {/* Blog Category Pages */}
        <Route path="blog/categories" element={<BlogCategoryPage />} />
        <Route path="blog/category/:category" element={<BlogCategoryPage />} />

        {/* Individual Blog Articles — data-driven from the CMS `articles` collection */}
        <Route path="blog/:slug" element={<BlogArticlePage />} />

        {/* SEO-friendly article URLs */}
        <Route path="articles/ai-fitness-revolution" element={<Navigate to="/blog/ai-fitness-revolution" replace />} />
        <Route path="articles/business-growth-tier2-cities" element={<Navigate to="/blog/business-growth-tier2-cities" replace />} />
        <Route path="articles/monsoon-fitness-training" element={<Navigate to="/blog/monsoon-fitness-training" replace />} />
        <Route path="articles/vegetarian-nutrition-planning" element={<Navigate to="/blog/vegetarian-nutrition-planning" replace />} />
        <Route path="articles/digital-marketing-fitness-professionals" element={<Navigate to="/blog/digital-marketing-fitness-professionals" replace />} />
        <Route path="articles/festival-season-client-management" element={<Navigate to="/blog/festival-season-client-management" replace />} />
        <Route path="articles/upi-digital-payments-guide" element={<Navigate to="/blog/upi-digital-payments-guide" replace />} />
        
        {/* Legacy blog post URLs for SEO */}
        <Route path="future-personal-training-ai-revolution" element={<Navigate to="/blog/ai-fitness-revolution" replace />} />
        <Route path="sustainable-fitness-business-tier-2-cities" element={<Navigate to="/blog/business-growth-tier2-cities" replace />} />
        <Route path="monsoon-fitness-client-engagement" element={<Navigate to="/blog/monsoon-fitness-training" replace />} />
        <Route path="vegetarian-nutrition-indian-clients" element={<Navigate to="/blog/vegetarian-nutrition-planning" replace />} />
        <Route path="digital-marketing-indian-fitness-professionals" element={<Navigate to="/blog/digital-marketing-fitness-professionals" replace />} />
        <Route path="festival-season-fitness-management" element={<Navigate to="/blog/festival-season-client-management" replace />} />
        <Route path="upi-payments-fitness-trainers" element={<Navigate to="/blog/upi-digital-payments-guide" replace />} />
        
        {/* Legal Pages */}
        <Route path="privacy" element={<PrivacyPolicyPage />} />
        <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="terms" element={<TermsOfServicePage />} />
        <Route path="terms-of-service" element={<TermsOfServicePage />} />
        <Route path="refund" element={<RefundPolicyPage />} />
        <Route path="refund-policy" element={<RefundPolicyPage />} />
        <Route path="subprocessors" element={<DataProcessingPage />} />
        <Route path="dpa" element={<DataProcessingPage />} />
        <Route path="gst" element={<GSTPage />} />
        <Route path="gst-policy" element={<GSTPage />} />
        <Route path="app-privacy-policy" element={<AppPrivacyPolicy />} />
        
        {/* Trial and Get Started */}
        <Route path="get-started" element={<Navigate to="/free-trial" replace />} />
        <Route path="free-trial" element={<FreeTrialPage />} />
        <Route path="start-trial" element={<Navigate to="/free-trial" replace />} />
        <Route path="for-coaches" element={<ForCoachesPage />} />
        <Route path="gyms" element={<GymsPage />} />
        <Route path="whats-new" element={<WhatsNewPage />} />
        <Route path="coaches" element={<Navigate to="/for-coaches" replace />} />

        {/* Competitor comparison pages */}
        <Route path="compare" element={<ComparePage />} />
        <Route path="alternatives/trainerize" element={<AlternativesTrainerizePage />} />
        <Route path="alternatives/truecoach" element={<AlternativesTrueCoachPage />} />
        <Route path="alternatives/everfit" element={<AlternativesEverfitPage />} />
        <Route path="alternatives/fitbudd" element={<AlternativesFitBuddPage />} />
        <Route path="alternatives/whatsapp-and-sheets" element={<AlternativesWhatsAppSheetsPage />} />
        <Route path="alternatives/:slug" element={<AlternativesCompetitorPage />} />
        <Route path="switch/:slug" element={<SwitchGuidePage />} />
        <Route path="compare/personal-trainer-software-pricing" element={<PricingComparisonPage />} />
        <Route path="white-label-fitness-app" element={<WhiteLabelAppPage />} />
        <Route path="us" element={<CountryPage code="us" />} />
        <Route path="uk" element={<CountryPage code="uk" />} />
        <Route path="au" element={<CountryPage code="au" />} />
        <Route path="ca" element={<CountryPage code="ca" />} />
        <Route path="ae" element={<CountryPage code="ae" />} />
        <Route path="features" element={<FeaturesHubPage />} />
        <Route path="features/:slug" element={<FeaturePage />} />
        <Route path="tools/coaching-software-cost-calculator" element={<CostCalculatorPage />} />
        <Route path="founding-coach" element={<FoundingCoachPage />} />
        <Route path="why-trainzilla" element={<WhyTrainzillaPage />} />
        
        {/* About and Company */}
        <Route path="about" element={<Navigate to="/about-us" replace />} />
        <Route path="company" element={<Navigate to="/about-us" replace />} />
      </Route>
      {/* Translated pages: /hi/…, /ar/…, /es/… (list in src/i18n/siteLocales.ts LOCALIZED_PATHS). */}
      <Route path="hi" element={<Layout />}>{localizedRoutes()}</Route>
      <Route path="ar" element={<Layout />}>{localizedRoutes()}</Route>
      <Route path="es" element={<Layout />}>{localizedRoutes()}</Route>
      {/* Catch-all: real "not found" page (noindex) inside the site layout */}
      <Route path="*" element={<Layout />}>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/** /hi/blog/x → /blog/x: that page only exists in English. */
function UntranslatedRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={splitLocale(pathname).path + search + hash} replace />;
}

/** Children of /hi, /ar and /es — keep in step with LOCALIZED_PATHS in src/i18n/siteLocales.ts. */
function localizedRoutes() {
  return [
    <Route key="index" index element={<HomePage />} />,
    <Route key="pricing" path="pricing" element={<PricingPage />} />,
    <Route key="gyms" path="gyms" element={<GymsPage />} />,
    <Route key="for-coaches" path="for-coaches" element={<ForCoachesPage />} />,
    <Route key="client-app" path="client-app" element={<ClientApp />} />,
    <Route key="ai-coach" path="ai-coach" element={<AICoachClientPage />} />,
    <Route key="features" path="features" element={<FeaturesHubPage />} />,
    <Route key="feature" path="features/:slug" element={<FeaturePage />} />,
    // Not translated: send /hi/blog/x etc. to the English page.
    <Route key="rest" path="*" element={<UntranslatedRedirect />} />,
  ];
}
